import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { HalfStarRating } from "@/components/reviews/HalfStarRating";
import { BooleanQuestion } from "@/components/reviews/BooleanQuestion";

export default function CreateReview() {
  useSeoMeta({ pageType: "review_form" });
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: venue } = useQuery({
    queryKey: ["venue-for-review", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("id, name, slug")
        .eq("slug", slug)
        .eq("status", "active")
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const [form, setForm] = useState({
    event_date: "",
    price_paid: "",
    overall_rating: 0,
    sound_rating: 0,
    staff_rating: 0,
    sound_tech: null as boolean | null,
    sound_tech_notes: "",
    technical_rider: null as boolean | null,
    technical_rider_notes: "",
    merchandising_area: null as boolean | null,
    merch_area_details: "",
    ticketing_required: null as boolean | null,
    ticketing_provider: "",
    backline_available: null as boolean | null,
    backline_details: "",
    audio_recording: null as boolean | null,
    poster_design: null as boolean | null,
    comment: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  if (!user) {
    return (
      <Layout>
        <div className="container py-16 text-center">
          <p>Inicia sesión para dejar una review de una sala.</p>
          <Button asChild className="mt-4">
            <Link to="/auth">Iniciar sesión</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  if (isAdmin) {
    return (
      <Layout>
        <div className="container py-16 text-center">
          <p className="text-lg font-medium">Los administradores no pueden enviar reviews</p>
          <p className="mt-2 text-sm text-muted-foreground">Tu rol es moderar y editar las reviews existentes.</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to={`/sala/${slug}/`}>Volver a la sala</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (form.overall_rating === 0) {
      toast({ title: "Selecciona una valoración general", variant: "destructive" });
      return;
    }
    if (!form.event_date) {
      toast({ title: "La fecha del evento es obligatoria", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from("reviews").insert({
        venue_id: venue!.id,
        user_id: user.id,
        event_date: form.event_date,
        price_paid: form.price_paid ? parseFloat(form.price_paid) : null,
        overall_rating: form.overall_rating,
        sound_rating: form.sound_rating || null,
        staff_rating: form.staff_rating || null,
        sound_tech: form.sound_tech,
        sound_tech_notes: form.sound_tech_notes || null,
        technical_rider: form.technical_rider,
        technical_rider_notes: form.technical_rider_notes || null,
        merchandising_area: form.merchandising_area,
        merch_area_details: form.merch_area_details || null,
        ticketing_required: form.ticketing_required,
        ticketing_provider: form.ticketing_provider || null,
        backline_available: form.backline_available,
        backline_details: form.backline_details || null,
        audio_recording: form.audio_recording,
        poster_design: form.poster_design,
      } as any);

      if (error) throw error;

      toast({
        title: "Review enviada",
        description: "Tu review será revisada por los administradores antes de publicarse.",
      });
      navigate(`/sala/${slug}/`);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="container max-w-2xl py-6">
        <Button asChild variant="ghost" size="sm" className="mb-4 gap-1">
          <Link to={`/sala/${slug}/`}>
            <ArrowLeft className="h-4 w-4" />
            Volver a {venue?.name ?? "la sala"}
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="font-display">Review de {venue?.name ?? "..."}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Ratings */}
              <div className="grid gap-4 sm:grid-cols-2">
                <HalfStarRating
                  label="Valoración general"
                  value={form.overall_rating}
                  onChange={(v) => update("overall_rating", v)}
                  required
                />
                <HalfStarRating
                  label="Sonido"
                  value={form.sound_rating}
                  onChange={(v) => update("sound_rating", v)}
                />
                <HalfStarRating
                  label="Staff"
                  value={form.staff_rating}
                  onChange={(v) => update("staff_rating", v)}
                />
              </div>

              {/* Event details */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Fecha del evento *</Label>
                  <Input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => update("event_date", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Precio pagado (€)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={form.price_paid}
                    onChange={(e) => update("price_paid", e.target.value)}
                  />
                </div>
              </div>

              {/* Boolean fields */}
              <div className="space-y-4 rounded-lg border p-4">
                <p className="text-sm font-medium">Información operativa</p>

                <div className="space-y-3">
                  <BooleanQuestion
                    label="¿Técnico de sonido propio?"
                    value={form.sound_tech}
                    onChange={(v) => update("sound_tech", v)}
                  />

                  <BooleanQuestion
                    label="¿Rider técnico disponible?"
                    value={form.technical_rider}
                    onChange={(v) => update("technical_rider", v)}
                    detailValue={form.technical_rider_notes}
                    onDetailChange={(v) => update("technical_rider_notes", v)}
                    detailPlaceholder="Notas sobre el rider técnico..."
                  />

                  <BooleanQuestion
                    label="¿Zona de merchandising?"
                    value={form.merchandising_area}
                    onChange={(v) => update("merchandising_area", v)}
                    detailValue={form.merch_area_details}
                    onDetailChange={(v) => update("merch_area_details", v)}
                    detailPlaceholder="Detalles sobre la zona de merch..."
                  />

                  <BooleanQuestion
                    label="¿Ticketing obligatorio?"
                    value={form.ticketing_required}
                    onChange={(v) => update("ticketing_required", v)}
                    detailValue={form.ticketing_provider}
                    onDetailChange={(v) => update("ticketing_provider", v)}
                    detailPlaceholder="Proveedor de ticketing..."
                  />

                  <BooleanQuestion
                    label="¿Hubo backline disponible?"
                    value={form.backline_available}
                    onChange={(v) => update("backline_available", v)}
                    detailValue={form.backline_details}
                    onDetailChange={(v) => update("backline_details", v)}
                    detailPlaceholder="Detalles del backline..."
                  />

                  <BooleanQuestion
                    label="¿Grabación de audio?"
                    value={form.audio_recording}
                    onChange={(v) => update("audio_recording", v)}
                  />

                  <BooleanQuestion
                    label="¿Diseño de cartelería?"
                    value={form.poster_design}
                    onChange={(v) => update("poster_design", v)}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Enviando..." : "Enviar review"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Tu review será revisada antes de publicarse. Tu nombre visible aparecerá junto a ella.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
